import dynamic from 'next/dynamic';
import React, { useMemo, useState } from 'react';

import { Divider } from '@reckon-web/divider';
import { LoadingDots } from '@reckon-web/loading';
import { Stack } from '@reckon-web/stack';
import { Text } from '@reckon-web/text';
import { useTheme } from '@reckon-web/theme';
import { Box, BoxProps } from '@reckon-web/box';
import { Flex } from '@reckon-web/flex';
import { ActionButton, Button } from '@reckon-web/button';
import { Tooltip } from '@reckon-web/tooltip';
import { AlertDialog } from '@reckon-web/modal-dialog';
import { useToasts } from '@reckon-web/toast';
import { makeId } from '@reckon-web/utils';

import type { Job } from '../../../hooks/use-query-job';
import { CommentTextRenderer } from '../../common/comment-text-renderer';
import { NamedUserAvatarWithUser } from '../../common/named-user-avatar';
import {
  GetMyDetailsQueryItem,
  useGetMyDetails,
} from '../../../data/useGetMyDetails';
import {
  JobCommentTypeEnum,
  JobCommentTypeEnumValue,
} from '../types/job-comment-type-enum';
import { DateUtils, formatDistanceOrDate } from '../../../utils/date-utils';
import { dateCompare, maybeify } from '../../../utils/sort';
import { useDeleteJobComment } from '../../../data/useDeleteJobComment';
import {
  UpdateJobCommentVariables,
  useUpdateJobComment,
} from '../../../data/useUpdateJobComment';
import type { GetJobCommentsItem } from '../../../data/useGetJobComments';

import { AddComment } from './add-comment';
// Weird dynamic import because
// Tribute doesn't work in SSR https://github.com/zurb/tribute/issues/622
// remove this dynamic import when the issue above is resolved (and a release is made with the fix in it)
const CommentTextEditor = dynamic(
  // ts ignore because this dynamic import is causing typescript to give the worlds second longest error message
  // about types not matching
  //@ts-ignore
  () =>
    import('../../common/comment-text-editor').then(
      (mod) => mod.CommentTextEditor
    ),
  { ssr: false }
);

export const Comments: React.FC<{
  job: Job;
  comments: GetJobCommentsItem[];
  canEditAndDelete?: boolean;
  testId?: string;
}> = ({ job, comments, canEditAndDelete = true, testId = `comments` }) => {
  const theme = useTheme();

  const sortedComments = useMemo(
    () =>
      [...(comments || [])].sort((a, b) =>
        maybeify(dateCompare, false)(a?.wfCreatedOnUtc, b?.wfCreatedOnUtc)
      ),
    [comments]
  );

  const { data: currentUser } = useGetMyDetails();

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateRows: '[list] minmax(0, 1fr) [divider] auto [add] auto',
        alignItems: 'stretch',
        justifyItems: 'stretch',
        maxHeight: '100%',
        overflow: 'hidden',
      }}
    >
      <div
        css={{
          overflowY: 'auto',
          overflowX: 'hidden',
          gridRow: 'list',
          minHeight: 0,
          paddingLeft: theme.spacing.xlarge,
          paddingRight: theme.spacing.xlarge,
          paddingTop: theme.spacing.xlarge,
          paddingBottom: theme.spacing.xlarge,
        }}
      >
        {sortedComments.length > 0 ? (
          <Stack gap="xlarge">
            {sortedComments.map((c) =>
              c && !c.wfDeletedOnUTC ? (
                <CommentItem
                  currentUser={currentUser || undefined}
                  key={c?.cloudId}
                  comment={c}
                  jobId={job.cloudId || ''}
                  canEditAndDelete={canEditAndDelete}
                  testId={makeId(testId, 'comment-item', c.cloudId)}
                />
              ) : (
                c?.wfDeletedOnUTC && (
                  <Box
                    background="muted"
                    paddingX="medium"
                    paddingY="small"
                    borderRadius="medium"
                    key={c?.cloudId}
                  >
                    <Text
                      color="dim"
                      size="small"
                      css={{ fontStyle: 'italic' }}
                    >
                      This comment was deleted
                    </Text>
                  </Box>
                )
              )
            )}
          </Stack>
        ) : (
          <Text size="small" color="dim">
            No comments added yet
          </Text>
        )}
      </div>
      <div css={{ gridRow: 'divider' }}>
        <Divider />
      </div>
      <div
        css={{
          gridRow: 'add',
          paddingLeft: theme.spacing.xlarge,
          paddingRight: theme.spacing.xlarge,
          paddingTop: theme.spacing.large,
          paddingBottom: theme.spacing.large,
        }}
      >
        <AddComment job={job} data-testid={makeId(testId, 'add-comment')} />
      </div>
    </div>
  );
};

// this could have gone in globals, but it's a wee bit specialist I think
// maybe it's not
const commentTypePalleteValues: Record<JobCommentTypeEnumValue, BoxProps> = {
  [JobCommentTypeEnum.valuesEnum.Comment]: { background: 'muted' },
  [JobCommentTypeEnum.valuesEnum.SignOffRequested]: {
    background: 'informative',
  },
  [JobCommentTypeEnum.valuesEnum.SignOffApproved]: {
    background: 'positive',
  },
  [JobCommentTypeEnum.valuesEnum.SignOffDeclined]: {
    background: 'critical',
  },
  [JobCommentTypeEnum.valuesEnum.SignOffWithdrawn]: {
    background: 'muted',
  },
  [JobCommentTypeEnum.valuesEnum.SignOffRevoked]: {
    background: 'cautious',
  },
};

const CommentItem: React.FC<{
  currentUser?: GetMyDetailsQueryItem;
  comment: GetJobCommentsItem;
  jobId: string;
  canEditAndDelete?: boolean;
  testId?: string;
}> = ({
  currentUser,
  comment,
  jobId,
  canEditAndDelete = true,
  testId = `comment-item`,
}) => {
  const theme = useTheme();
  const { addToast } = useToasts();
  // strikes me maybe we should do this prompting, updating and deleting in the parent
  const [deleteComment] = useDeleteJobComment(jobId);
  const [updateComment] = useUpdateJobComment();

  const isCurrentUserComment =
    comment.wfCreatedBy === currentUser?.user?.cloudId;

  const canEdit =
    canEditAndDelete && isCurrentUserComment && comment.commentType
      ? comment.commentType === JobCommentTypeEnum.valuesEnum.Comment
      : false;

  // Apply background colours depending on CommentType
  const boxProps =
    commentTypePalleteValues[
      comment.commentType || JobCommentTypeEnum.valuesEnum.Comment
    ];

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isPromptingDelete, setIsPromptingDelete] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [commentText, setCommentText] = useState<string>(
    () => comment.comment || ''
  );
  const startEdit = () => {
    setIsEditing(true);
  };
  const saveEdit = async () => {
    setIsUpdating(true);
    const input: UpdateJobCommentVariables = {
      input: {
        cloudId: comment.cloudId,
        comment: commentText,
        jobId,
      },
    };
    const gqlResult = await updateComment({
      variables: { input: { ...input.input } },
    });
    if (
      gqlResult &&
      gqlResult.data &&
      gqlResult.data.updateJobComment &&
      (!gqlResult.data.updateJobComment.validationMessage ||
        gqlResult.data.updateJobComment.validationMessage.length === 0)
    ) {
      // happy path
      // err?
      setIsEditing(false);
      setIsUpdating(false);
    } else {
      // sad path. Could see if there was a validation error for the comment field
      // but we'll just generic toast and move on
      addToast({
        title: `Comment not updated`,
        message: `Workflow failed to update the comment. Try again or contact support.`,
        tone: 'critical',
      });
    }
  };
  const cancelEdit = () => {
    setIsEditing(false);
    setCommentText(comment.comment || '');
  };
  const promptDelete = () => {
    setIsPromptingDelete(true);
  };
  const doDelete = async () => {
    setIsDeleting(true);
    const gqlResult = await deleteComment({
      variables: { cloudId: comment.cloudId },
    });
    if (
      gqlResult &&
      gqlResult.data &&
      gqlResult.data.deleteJobComment &&
      (!gqlResult.data.deleteJobComment.validationMessage ||
        gqlResult.data.deleteJobComment.validationMessage.length === 0)
    ) {
      // happy path
      // actually, nothing to do
    } else {
      // sad path. Could see if there was a validation error for the comment field
      // but we'll just generic toast and move on
      addToast({
        title: `Comment not deleted`,
        message: `Workflow failed to delete the comment. Try again or contact support.`,
        tone: 'critical',
      });
    }

    setIsDeleting(false);
    setIsPromptingDelete(false);
  };

  const promptDeleteActions = {
    confirm: { label: 'Delete', action: () => doDelete(), loading: isDeleting },
    cancel: { label: 'Cancel', action: () => setIsPromptingDelete(false) },
  };
  // created and modified are not always the exact same time, even for newly created comments
  // and perhaps in the future, we might want to give people a minute or two to edit their comment
  // before marking it as Edited
  const oneSecond = 1000;
  const commentWasEdited =
    comment.wfCreatedOnUtc &&
    comment.WfModifiedOnUtc &&
    Math.abs(
      comment.wfCreatedOnUtc.getTime() - comment.WfModifiedOnUtc.getTime()
    ) > oneSecond;

  // this is super busted out because I had gremlins in the code so I've taken it back to basics
  const toltipCreated = `Created: ${DateUtils.formatDateTimeShort(
    comment.wfCreatedOnUtc
  )}`;
  const tooltipEdited = commentWasEdited
    ? `Edited: ${DateUtils.formatDateTimeShort(comment.WfModifiedOnUtc)}`
    : '';

  // Override of button styles to make it xsmall, needed for this custom comments layout
  const XSmallButtonStyle = {
    fontSize: theme.typography.fontSize.xsmall,
    paddingLeft: theme.spacing.small,
    paddingRight: theme.spacing.small,
    paddingTop: theme.spacing.xsmall,
    paddingBottom: theme.spacing.xsmall,
    height: theme.sizing.xsmall,
  };

  return (
    <React.Fragment>
      <Flex direction="column" gap="small" data={{ testid: testId }}>
        <Flex justifyContent="space-between" gap="small" alignItems="baseline">
          {comment.wfCreatedBy && (
            <NamedUserAvatarWithUser
              userId={comment.wfCreatedBy}
              avatarSize="xsmall"
              textSize="small"
              nameSuffix={isCurrentUserComment ? '(me)' : undefined}
              testId={makeId(testId, 'avatar')}
            />
          )}
          <Flex alignItems="center" gap="xsmall">
            {comment.wfCreatedOnUtc && (
              <Tooltip content={toltipCreated}>
                <Text
                  inline
                  color="muted"
                  size="xsmall"
                  align="end"
                  data={{ testid: makeId(testId, 'comment') }}
                >
                  {formatDistanceOrDate(comment.wfCreatedOnUtc, {
                    includeSeconds: true,
                    addSuffix: true,
                  })}
                </Text>
              </Tooltip>
            )}
            {commentWasEdited && (
              <Tooltip content={tooltipEdited}>
                <Text inline color="muted" size="xsmall">
                  {' '}
                  (edited)
                </Text>
              </Tooltip>
            )}
          </Flex>
        </Flex>
        {isUpdating ? (
          <Flex paddingY="large">
            <LoadingDots
              size="small"
              label="Saving"
              data-testid={makeId(testId, 'saving')}
            />
          </Flex>
        ) : isEditing ? (
          <CommentTextEditor
            value={commentText}
            onChange={(value) => setCommentText(value)}
            invalid={!commentText}
            data-testid={makeId(testId, 'comment-text')}
          />
        ) : (
          <Box {...boxProps} borderRadius="medium" padding="medium">
            <CommentTextRenderer
              value={commentText}
              data-testid={makeId(testId, 'comment-text-renderer')}
            />
          </Box>
        )}
        {canEdit && (
          <Flex alignItems="center" justifyContent="flex-end" gap="xsmall">
            {!isEditing && (
              <ActionButton
                label="Delete"
                weight="subtle"
                onClick={promptDelete}
                disabled={isUpdating}
                css={{ ...XSmallButtonStyle }}
                data-testid={makeId(testId, 'delete')}
              />
            )}
            {!isEditing && (
              <ActionButton
                label="Edit"
                onClick={startEdit}
                disabled={isUpdating}
                css={{
                  ...XSmallButtonStyle,
                }}
                data-testid={makeId(testId, 'edit')}
              />
            )}
            {isEditing && (
              <Button
                label="Cancel"
                weight="none"
                tone="passive"
                size="small"
                onClick={cancelEdit}
                disabled={isUpdating}
                css={{ ...XSmallButtonStyle }}
                data-testid={makeId(testId, 'cancel')}
              />
            )}
            {isEditing && (
              <Button
                label="Save"
                weight="subtle"
                size="small"
                onClick={saveEdit}
                disabled={isUpdating}
                css={{ ...XSmallButtonStyle }}
                data-testid={makeId(testId, 'save')}
              />
            )}
          </Flex>
        )}
      </Flex>
      <AlertDialog
        actions={promptDeleteActions}
        isOpen={isPromptingDelete}
        title="Delete comment"
        tone="critical"
        data-testid={makeId(testId, 'alert')}
      >
        <Text>Are you sure? This action cannot be reversed.</Text>
      </AlertDialog>
    </React.Fragment>
  );
};
